import React, {useMemo, useState} from "react";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Grid from "@mui/material/Grid";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import {CONTACT_US} from "../../../commonUtils/globalAPISettings";
import {getRequest, setHeader} from "../../../repository/genericRepository";
import {store} from "../../../redux/store";
import AddressIcon from "../../../assets/address_icon.png";
import telephoneIcon from "../../../assets/telephone_icon.png";
import emailIcon from "../../../assets/email_icon.png";
import faxIcon from "../../../assets/fax_icon.png";
import {ContactStyle} from "./ContactUsDialogStyles";
import {Loader} from "components/FormComponents/Loader/Loader";
import DialogTitleCommon, {DialogCloseCommon} from "../DialogTitleCommon";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import {
  handleAxiosErrors,
  initialMessage,
  isValidObject,
  loggedMessage,
  message,
} from "commonUtils/utils";
import {
  Alerts,
  API_TIMEOUTS_MILLISECONDS,
  PageNames,
} from "commonUtils/enumerations";
import FormValidationAlert from "components/FormComponents/Alert/FormValidationAlert";

interface ContactUsInfo {
  addressLine1: string;
  addressLine2: string;
  addressLine3: string;
  addressLine4: string;
  telephone: string;
  email: string;
  fax: string;
  availability: string;
}
const ContactUsDialog = ({
  contactUsOpen,
  setContactView,
}: {
  contactUsOpen: boolean;
  setContactView: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const [contactInfo, setContactInfo] = useState<ContactUsInfo>();
  const [loader, setLoader] = useState<boolean>(false);
  const [showAlert, setShowAlert] = React.useState<boolean>(false);
  const [serverValidation, setServerValidation] =
    React.useState<message>(initialMessage);

  const getContactUsInfo = useMemo(
    () => async () => {
      try {
        const authState = store.getState();
        setLoader(true);
        const APIHeaders = {
          Authorization: `Bearer ${authState.auth?.authDetails?.token}`,
        };
        const responseData = await getRequest({
          url: CONTACT_US,
          headers: setHeader(APIHeaders),
          timeout: API_TIMEOUTS_MILLISECONDS.CONTACT_US,
          params: {},
        });
        if (isValidObject(responseData)) {
          setContactInfo(responseData);
        }
        setLoader(false);
      } catch (error: any) {
        loggedMessage(error, serverValidation?.message, PageNames.DASHBOARD);
        handleAxiosErrors(error, setShowAlert, setServerValidation);
        setLoader(false);
      }
    },
    [contactInfo],
  );
  React.useEffect(() => {
    try {
      getContactUsInfo();
    } catch (error: any) {
      loggedMessage(error, serverValidation?.message, PageNames.DASHBOARD);
      handleAxiosErrors(error, setShowAlert, setServerValidation);
      setLoader(false);
    }
  }, []);

  const handleClose = () => {
    setContactView(false);
  };

  return (
    <>
      <Loader open={loader} />
      {contactInfo && (
        <Dialog open={contactUsOpen} fullWidth maxWidth="sm">
          <DialogTitleCommon title="Contact : Phone & Email" />
          <DialogCloseCommon onClick={handleClose} />
          <DialogContent>
            <Grid>
              {showAlert && serverValidation?.message && (
                <FormValidationAlert
                  closeIcon={true}
                  showAlertBox={showAlert}
                  setShowAlertBox={setShowAlert}
                  severity={
                    (serverValidation?.isSuccessMessage ?? false)
                      ? Alerts.SUCCESS
                      : (serverValidation?.isValidationMessage ?? false)
                        ? Alerts.INFO
                        : Alerts.ERROR
                  }
                  message={serverValidation?.message}
                />
              )}
              <Grid item sx={ContactStyle.GridItem}>
                <img src={AddressIcon} alt="my" style={ContactStyle.Icon} />
                <Box>
                  <Typography component="p" sx={ContactStyle.TypoGraphyTitle}>
                    ADDRESS
                  </Typography>
                  <Typography component="p" sx={ContactStyle.TypoGraphyAddress}>
                    {contactInfo?.addressLine1} <br />
                    {contactInfo?.addressLine2}
                    <br />
                    {contactInfo?.addressLine3}
                    <br />
                    {contactInfo?.addressLine4}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            <Grid>
              <Grid item sx={ContactStyle.GridItem}>
                <img src={telephoneIcon} alt="my" style={ContactStyle.Icon} />
                <Box>
                  <Typography component="p" sx={ContactStyle.TypoGraphyTitle}>
                    TELEPHONE
                  </Typography>
                  <Box sx={{marginBottom: "1.25rem"}}>
                    {contactInfo?.telephone}
                  </Box>
                </Box>
              </Grid>
            </Grid>
            <Grid>
              <Grid item sx={ContactStyle.GridItem}>
                <img src={emailIcon} alt="my" style={ContactStyle.Icon} />
                <Box>
                  <Typography component="p" sx={ContactStyle.TypoGraphyTitle}>
                    EMAIL
                  </Typography>
                  <Box sx={{marginBottom: "1.25rem"}}>
                    <Link
                      href={`mailto:${contactInfo?.email}`}
                      sx={ContactStyle.Link}>
                      {contactInfo?.email}
                    </Link>
                  </Box>
                </Box>
              </Grid>
            </Grid>
            <Grid>
              <Grid item sx={ContactStyle.GridItem}>
                <img src={faxIcon} alt="my" style={ContactStyle.Icon} />
                <Box>
                  <Typography component="p" sx={ContactStyle.TypoGraphyTitle}>
                    FAX
                  </Typography>
                  <Box sx={{marginBottom: "1.25rem"}}>{contactInfo?.fax}</Box>
                </Box>
              </Grid>
            </Grid>
            <Grid>
              <Grid item sx={ContactStyle.GridItem}>
                <AccessTimeIcon />
                <Box>
                  <Box sx={ContactStyle.extratext}>
                    {contactInfo?.availability}
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default ContactUsDialog;
